import { useState } from 'react';

import { CloseOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { CardCodes, CARD_CODES, CorporateCardConnection } from '@revenuemarket/bank';
import { Button, Dropdown, Flex, Input as CDSInput, Text, Colors } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { formatCompanyTaxNumber, deformatCompanyTaxNumber } from '@revenuemarket/utils';
import { Alert } from 'antd';

import { BusinessRegistrationNumberInfo } from '~apis/bank/getBusinessRegistrationNumbers';
import CommonModal from '~components/ui/Modal/CommonModal';
import { loginCardByID } from '~hooks/queries/card/query';
import { useBusinessRegistrationNumbers } from '~hooks/queries/useBusinessRegistrationNumbers';
import { useUpdateCorporateCardBusinessRegNo } from '~hooks/queries/useUpdateCorporateCardConnection';
import { useCompanyId } from '~hooks/useCompanyId';
import { validateBusinessRegNumber } from '~utils/businessRegNumber/validator';

import { getCardLoginErrorMessage } from './cardLoginErrorMessages';

interface CardEditModalContentProps {
  isOpen: boolean;
  onClose: () => void;
  connection: CorporateCardConnection;
  organizationCode: CardCodes;
}

type EditMode = 'password' | 'businessNumber' | null;

export const CardEditModalContent: React.FC<CardEditModalContentProps> = ({
  isOpen,
  onClose,
  connection,
  organizationCode,
}) => {
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [password, setPassword] = useState('');
  const [businessNumber, setBusinessNumber] = useState(connection.businessRegNo || '');
  const [businessNumberError, setBusinessNumberError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const { companyId } = useCompanyId();
  const { data: businessNumbersData } = useBusinessRegistrationNumbers();
  const { mutateAsync: updatePassword, isLoading: isUpdatingPassword } = loginCardByID();
  const { mutateAsync: updateBusinessRegNo, isLoading: isUpdatingBusinessRegNo } =
    useUpdateCorporateCardBusinessRegNo();

  const organizationName = CARD_CODES[organizationCode];

  const handlePasswordSave = async () => {
    setError(null);

    if (!password) {
      setError('비밀번호를 입력해주세요');
      return;
    }

    const response = await updatePassword({
      companyId,
      loginId: connection.loginId,
      password: password,
      organizationCode: organizationCode,
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('비밀번호가 수정되었습니다.');
      setEditMode(null);
      setPassword('');
    } else {
      setError(getCardLoginErrorMessage(response.ErrorCode));
    }
  };

  const handleBusinessNumberChange = (value: string) => {
    setBusinessNumber(value);

    if (value.length > 0) {
      const validation = validateBusinessRegNumber(value);
      setBusinessNumberError(validation.error);
    } else {
      setBusinessNumberError(undefined);
    }
  };

  const handleBusinessNumberSelect = (value: string) => {
    setBusinessNumber(value);
    const validation = validateBusinessRegNumber(value);
    setBusinessNumberError(validation.error);
  };

  const handleBusinessNumberSave = async () => {
    if (!businessNumber) {
      return;
    }

    const validation = validateBusinessRegNumber(businessNumber);
    if (!validation.isValid) {
      setBusinessNumberError(validation.error);
      return;
    }

    try {
      await updateBusinessRegNo({
        connectionId: connection.id,
        businessRegNo: businessNumber,
      });
      setEditMode(null);
      setBusinessNumberError(undefined);
    } catch (error) {
      // 에러는 hook에서 Toast로 처리됨
    }
  };

  const handleCancel = () => {
    setEditMode(null);
    setPassword('');
    setError(null);
    // 사업자번호는 초기값으로 복원
    setBusinessNumber(connection.businessRegNo || '');
    setBusinessNumberError(undefined);
  };

  return (
    <CommonModal
      open={isOpen}
      onCancel={onClose}
      closeIcon={<CloseOutlined style={{ fontSize: 18 }} />}
      title={`${organizationName} 로그인 정보 수정`}
      footer={
        <Flex justify="flex-end" style={{ marginTop: 8 }}>
          <Button
            onClick={onClose}
            style={{
              width: 60,
              height: 36,
              borderRadius: 10,
              border: `1px solid ${Colors.grayOpacity12}`,
              backgroundColor: Colors.white,
            }}
          >
            <Text textStyle="16_500" style={{ color: Colors.gray700 }}>
              닫기
            </Text>
          </Button>
        </Flex>
      }
      width={400}
      centered
    >
      <Flex direction="column" gap={16}>
        {/* 아이디 */}
        <Flex
          direction="column"
          gap={8}
          style={{
            paddingBottom: 12,
            borderBottom: `1px solid ${Colors.grayOpacity07}`,
          }}
        >
          <Text textStyle="14_400" style={{ color: Colors.gray800 }}>
            아이디
          </Text>
          <Text textStyle="16_500" style={{ color: Colors.gray800 }}>
            {connection.loginName}
          </Text>
        </Flex>

        {/* 비밀번호 */}
        <Flex
          gap={8}
          align="flex-start"
          style={{
            paddingBottom: 12,
            borderBottom: `1px solid ${Colors.grayOpacity07}`,
          }}
        >
          <Flex direction="column" gap={8} style={{ flex: 1 }}>
            <Text textStyle="14_400" style={{ color: Colors.gray800 }}>
              비밀번호
            </Text>
            {editMode === 'password' ? (
              <>
                <CDSInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="새 비밀번호 입력"
                  type={passwordVisible ? 'text' : 'password'}
                  suffix={
                    passwordVisible ? (
                      <EyeOutlined
                        onClick={() => setPasswordVisible(false)}
                        style={{ cursor: 'pointer', fontSize: 16 }}
                      />
                    ) : (
                      <EyeInvisibleOutlined
                        onClick={() => setPasswordVisible(true)}
                        style={{ cursor: 'pointer', fontSize: 16 }}
                      />
                    )
                  }
                  inputSize="default"
                />
                <Flex gap={8}>
                  <Button
                    buttonSize="default"
                    onClick={handleCancel}
                    style={{ height: 36, borderRadius: 10 }}
                  >
                    취소
                  </Button>
                  <Button
                    buttonType="primary"
                    buttonSize="default"
                    onClick={handlePasswordSave}
                    loading={isUpdatingPassword}
                    style={{ height: 36, borderRadius: 10 }}
                  >
                    저장
                  </Button>
                </Flex>
                {error && <Alert message={error} type="error" showIcon style={{ marginTop: 8 }} />}
              </>
            ) : (
              <Text textStyle="16_500" style={{ color: Colors.gray800 }}>
                ••••••••••••
              </Text>
            )}
          </Flex>
          {editMode !== 'password' && (
            <Button
              onClick={() => setEditMode('password')}
              style={{
                backgroundColor: Colors.grayOpacity04,
                height: 36,
                borderRadius: 10,
                padding: '0 12px',
                border: 'none',
                minWidth: 'auto',
              }}
            >
              <Text textStyle="16_500" style={{ color: Colors.gray700 }}>
                수정하기
              </Text>
            </Button>
          )}
        </Flex>

        {/* 사업자등록번호 */}
        <Flex gap={16} align="flex-start">
          <Flex direction="column" gap={8} style={{ flex: 1 }}>
            <Text textStyle="14_400" style={{ color: Colors.gray800 }}>
              사업자등록번호
            </Text>
            {editMode === 'businessNumber' ? (
              <>
                <Dropdown
                  menu={{
                    items:
                      businessNumbersData?.Data?.data?.businessRegNumbers?.map(
                        (item: BusinessRegistrationNumberInfo) => ({
                          key: item.businessRegNumber,
                          label: item.companyName
                            ? `${formatCompanyTaxNumber(item.businessRegNumber)} (${
                                item.companyName
                              })`
                            : formatCompanyTaxNumber(item.businessRegNumber),
                          onClick: () => handleBusinessNumberSelect(item.businessRegNumber),
                        }),
                      ) || [],
                  }}
                  trigger={['click']}
                >
                  <CDSInput
                    value={businessNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length > 10) {
                        return;
                      }
                      handleBusinessNumberChange(value);
                    }}
                    formatter={formatCompanyTaxNumber}
                    deformatter={deformatCompanyTaxNumber}
                    inputSize="default"
                    inputStatus={businessNumberError ? 'error' : undefined}
                    additionalText={businessNumberError}
                    placeholder="사업자등록번호 10자리"
                  />
                </Dropdown>
                <Flex gap={8}>
                  <Button
                    buttonSize="default"
                    onClick={handleCancel}
                    style={{ height: 36, borderRadius: 10 }}
                  >
                    취소
                  </Button>
                  <Button
                    buttonType="primary"
                    buttonSize="default"
                    onClick={handleBusinessNumberSave}
                    loading={isUpdatingBusinessRegNo}
                    disabled={!!businessNumberError || !businessNumber}
                    style={{ height: 36, borderRadius: 10 }}
                  >
                    저장
                  </Button>
                </Flex>
              </>
            ) : (
              <Text textStyle="16_500" style={{ color: Colors.gray800 }}>
                {formatCompanyTaxNumber(businessNumber)}
              </Text>
            )}
          </Flex>
          {editMode !== 'businessNumber' && (
            <Button
              onClick={() => setEditMode('businessNumber')}
              style={{
                backgroundColor: Colors.grayOpacity04,
                height: 36,
                borderRadius: 10,
                padding: '0 12px',
                border: 'none',
                minWidth: 'auto',
              }}
            >
              <Text textStyle="16_500" style={{ color: Colors.gray700 }}>
                수정하기
              </Text>
            </Button>
          )}
        </Flex>
      </Flex>
    </CommonModal>
  );
};
